import { type ReactNode, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Link } from '@tanstack/react-router';
import { T, useGT, Num, Var } from 'gt-react';
import {
  ArrowClockwiseIcon,
  ArrowSquareOutIcon,
  DownloadSimpleIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import {
  VENDOR_PORTAL_FAILED_RUNS_DAYS,
  type VendorPortalFailedRun,
} from '@erp/rpc';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import paths from '@/paths';
import {
  useMarkVendorPortalFailedRunsSeen,
  useVendorPortalFailedRuns,
} from './useVendorPortalFailedRuns';

const BADGE_CAP = 99;
const ROW_DATETIME_FORMAT = 'MM/dd/yyyy HH:mm';

type Account = VendorPortalFailedRun['accounts'][number];
type AccountStatus = Account['status'];
type SummaryFile = NonNullable<VendorPortalFailedRun['summary']>;

/**
 * The failed-runs icon for Pricing → Vendor Portals: a badge with the failed runs started
 * by anyone in the organization since this user last looked, and a popover listing the
 * last 30 days' failed runs with their accounts grouped by reason. Opening the popover
 * clears this user's badge only.
 */
export function VendorPortalFailedRuns({
  onDownloadSummary,
}: {
  onDownloadSummary: (file: SummaryFile) => void;
}) {
  const gt = useGT();
  const [open, setOpen] = useState(false);
  const { data, isLoading, isError, refetch } = useVendorPortalFailedRuns();
  const { mutate: markSeen } = useMarkVendorPortalFailedRunsSeen();

  const count = data?.unseenCount ?? 0;
  const badge = count > BADGE_CAP ? `${BADGE_CAP}+` : String(count);

  // Marked seen once per opening, when the list is on screen: opened before the first
  // load finished, or after a retry, counts once the runs show. A failed load is never
  // marked, so it can't clear the badge on failures the user didn't see.
  const showingRuns = open && data !== undefined;
  useEffect(() => {
    if (showingRuns) markSeen({});
  }, [showingRuns, markSeen]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={gt('Failed vendor portal runs', {
            context:
              'aria label for the Vendor Portals icon button that opens the list of runs with failed accounts',
          })}
          title={gt('Failed vendor portal runs', {
            context:
              'Tooltip for the Vendor Portals icon button that opens the list of runs with failed accounts',
          })}
        >
          <WarningCircleIcon className="h-4 w-4" />
          {count > 0 && (
            <span
              data-testid="vendor-portal-failed-runs-badge"
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium leading-none text-white"
            >
              {badge}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[28rem] p-0">
        <div className="border-b px-3 py-2 text-sm font-medium">
          <T context="Header of the Vendor Portals failed-runs popover">
            Runs with failed accounts (last{' '}
            <Num>{VENDOR_PORTAL_FAILED_RUNS_DAYS}</Num> days)
          </T>
        </div>
        <div className="max-h-[32rem] overflow-y-auto">
          <FailedRunList
            isLoading={isLoading}
            isError={isError && !data}
            onRetry={() => void refetch()}
            runs={data?.items ?? []}
            onDownloadSummary={onDownloadSummary}
            onNavigate={() => setOpen(false)}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}

function FailedRunList({
  isLoading,
  isError,
  onRetry,
  runs,
  onDownloadSummary,
  onNavigate,
}: {
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  runs: VendorPortalFailedRun[];
  onDownloadSummary: (file: SummaryFile) => void;
  onNavigate: () => void;
}) {
  if (isLoading) {
    return (
      <p className="px-3 py-6 text-center text-sm text-muted-foreground">
        <T context="Vendor Portals failed-runs popover loading state">
          Loading...
        </T>
      </p>
    );
  }
  if (isError) {
    return (
      <div className="space-y-2 px-3 py-6 text-center text-sm text-muted-foreground">
        <p>
          <T context="Vendor Portals failed-runs popover error state when the list could not load">
            Could not load the failed runs.
          </T>
        </p>
        <Button size="sm" variant="outline" onClick={onRetry}>
          <ArrowClockwiseIcon className="h-4 w-4" />
          <T context="Vendor Portals failed-runs popover button that retries loading the list">
            Try Again
          </T>
        </Button>
      </div>
    );
  }
  if (runs.length === 0) {
    return (
      <p className="px-3 py-6 text-center text-sm text-muted-foreground">
        <T context="Vendor Portals failed-runs popover empty state">
          No runs with failed accounts in the last{' '}
          <Num>{VENDOR_PORTAL_FAILED_RUNS_DAYS}</Num> days.
        </T>
      </p>
    );
  }
  return (
    <ul className="divide-y">
      {runs.map((run) => (
        <FailedRunRow
          key={run.workflowRunId}
          run={run}
          onDownloadSummary={onDownloadSummary}
          onNavigate={onNavigate}
        />
      ))}
    </ul>
  );
}

function FailedRunRow({
  run,
  onDownloadSummary,
  onNavigate,
}: {
  run: VendorPortalFailedRun;
  onDownloadSummary: (file: SummaryFile) => void;
  onNavigate: () => void;
}) {
  const summary = run.summary;
  return (
    <li className="space-y-2 px-3 py-2 text-sm">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-medium">{run.vendor}</span>
        <span className="text-xs text-muted-foreground">
          {format(new Date(run.reportedAt), ROW_DATETIME_FORMAT)}
        </span>
      </div>
      <div className="text-xs text-muted-foreground">
        <RunStarter startedBy={run.startedBy} /> ·{' '}
        <T context="Vendor Portals failed-runs popover: how many of the run's accounts need attention">
          <Num>{run.counts.total}</Num> accounts need attention
        </T>
      </div>
      {ACCOUNT_GROUPS.map(({ status, heading }) => (
        <AccountGroup
          key={status}
          status={status}
          heading={heading}
          accounts={run.accounts.filter((account) => account.status === status)}
        />
      ))}
      <div className="flex items-center gap-3 text-xs">
        {summary && (
          <Button
            size="sm"
            variant="outline"
            className="h-7"
            onClick={() => onDownloadSummary(summary)}
          >
            <DownloadSimpleIcon className="h-4 w-4" />
            <T context="Vendor Portals failed-runs popover button that downloads the run's summary file">
              Summary File
            </T>
          </Button>
        )}
        <Link
          to={paths.authenticated.workflows.run}
          params={{
            workflowId: run.workflowId,
            workflowRunId: run.workflowRunId,
          }}
          onClick={onNavigate}
          className="inline-flex items-center gap-1 hover:underline"
        >
          <T context="Vendor Portals failed-runs popover link to the workflow run">
            View run
          </T>
          <ArrowSquareOutIcon className="h-3 w-3" />
        </Link>
      </div>
    </li>
  );
}

function RunStarter({
  startedBy,
}: {
  startedBy: VendorPortalFailedRun['startedBy'];
}) {
  if (!startedBy) {
    return (
      <T context="Vendor Portals failed-runs popover: the run was started by the system, not a person">
        Started automatically
      </T>
    );
  }
  return (
    <T context="Vendor Portals failed-runs popover: who started the run">
      Started by <Var>{startedBy.name}</Var>
    </T>
  );
}

/** How the popover groups accounts, in order: Failed Accounts first. */
const ACCOUNT_GROUPS: readonly {
  status: AccountStatus;
  heading: ReactNode;
}[] = [
  {
    status: 'could_not_open',
    heading: (
      <T context="Vendor Portals failed-runs popover group: accounts the agent could not log in to or open">
        Couldn&apos;t get in
      </T>
    ),
  },
  {
    status: 'download_failed',
    heading: (
      <T context="Vendor Portals failed-runs popover group: accounts whose price list download failed">
        Download failed
      </T>
    ),
  },
  {
    status: 'no_price_lists',
    heading: (
      <T context="Vendor Portals failed-runs popover group: accounts that showed no price lists">
        No Price Lists
      </T>
    ),
  },
];

function AccountGroup({
  status,
  heading,
  accounts,
}: {
  status: AccountStatus;
  heading: ReactNode;
  accounts: Account[];
}) {
  if (accounts.length === 0) return null;
  return (
    <section
      className="rounded-md border px-2 py-1.5"
      data-testid={`failed-run-group-${status}`}
    >
      <div className="text-xs font-medium">
        {heading} ({accounts.length})
      </div>
      <ul className="mt-1 space-y-1">
        {/* Two identical lines would share a key, but they render identically. */}
        {accounts.map((account) => (
          <li
            key={`${account.accountNumber}:${account.accountName}:${account.error}`}
            className="text-xs"
          >
            <span className="font-mono">{account.accountNumber}</span>
            {account.accountName && (
              <span className="ml-2 text-muted-foreground">
                {account.accountName}
              </span>
            )}
            {account.error && (
              <div className="text-destructive">{account.error}</div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
